import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { EChart } from "@kbox-labs/react-echarts";
import { alpha, useTheme } from "@mui/material";
import { useAuth } from "react-oidc-context";
import {
  getLlmCallDetail,
  LlmCallDetail,
  LlmUsageSummary,
  LlmUsageTimelinePoint,
} from "../utils/backend-utils";

interface SessionCostPopoverProps {
  sessionId?: string | null;
  llmUsage?: LlmUsageSummary | null;
  onScrollToMessage?: (sourceMessageTs: string) => void;
}

function formatFeatureLabel(feature: string): string {
  return feature.replace(/^qinterrogator_/, "").replace(/_/g, " ");
}

function formatUsd(value?: number): string {
  const amount = typeof value === "number" ? value : 0;
  if (amount === 0) return "$0.00";
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
}

function formatCallTime(ts?: string): string {
  if (!ts) return "—";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function truncateText(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1)}…`;
}

function PromptResponseBlock({ label, text }: { label: string; text: string }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          maxHeight: 180,
          overflow: "auto",
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          fontFamily: "monospace",
          fontSize: "0.68rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text || "—"}
      </Box>
    </Box>
  );
}

function CallDetailPanel({
  row,
  sessionId,
  detail,
  loading,
  error,
}: {
  row: LlmUsageTimelinePoint;
  sessionId?: string | null;
  detail: LlmCallDetail | null;
  loading: boolean;
  error: string | null;
}) {
  const context = row.utterance_preview?.trim();
  const turnLabel =
    row.turn_index != null
      ? `Turn ${row.turn_index}${row.chunk_index != null ? ` · chunk ${row.chunk_index}` : ""}`
      : null;

  return (
    <Box sx={{ px: 1.5, py: 1, bgcolor: "action.hover", borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Token breakdown
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
        prompt {row.prompt_tokens ?? detail?.prompt_tokens ?? 0} · output{" "}
        {row.output_tokens ?? detail?.output_tokens ?? 0}
        {(row.thinking_tokens ?? detail?.thinking_tokens ?? 0) > 0
          ? ` · thinking ${row.thinking_tokens ?? detail?.thinking_tokens}`
          : ""}
        {" · "}total {row.total_tokens ?? detail?.total_tokens ?? 0}
      </Typography>
      {(row.model || detail?.model) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
          Model: {row.model || detail?.model}
        </Typography>
      )}
      {turnLabel && (
        <Typography variant="caption" color="text.secondary" display="block">
          {turnLabel}
        </Typography>
      )}
      {context && (
        <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          Context: {context}
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Loading prompt/response…
          </Typography>
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && !row.call_id && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Full prompt/response not available for calls recorded before inspection logging was enabled.
        </Typography>
      )}

      {!loading && !error && row.call_id && !sessionId && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Session id required to load call detail.
        </Typography>
      )}

      {!loading && detail && (
        <>
          {detail.truncated && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
              Stored text was truncated at the server limit.
            </Typography>
          )}
          <PromptResponseBlock label="Prompt" text={detail.prompt || ""} />
          <PromptResponseBlock label="Response" text={detail.response || ""} />
        </>
      )}
    </Box>
  );
}

function CallLogRow({
  row,
  index,
  expanded,
  sessionId,
  onToggle,
  onScrollToMessage,
}: {
  row: LlmUsageTimelinePoint;
  index: number;
  expanded: boolean;
  sessionId?: string | null;
  onToggle: () => void;
  onScrollToMessage?: (sourceMessageTs: string) => void;
}) {
  const auth = useAuth();
  const [detail, setDetail] = useState<LlmCallDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    if (!row.call_id || !sessionId || !auth.isAuthenticated) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getLlmCallDetail(sessionId, row.call_id, auth)
      .then((result) => {
        if (!cancelled) {
          setDetail(result);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setDetail(null);
          setError(err.message || "Failed to load call detail");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, row.call_id, sessionId, auth]);

  const canJump = Boolean(row.source_message_ts && onScrollToMessage);
  const contextPreview = row.utterance_preview?.trim() || "—";

  return (
    <>
      <TableRow
        hover
        onClick={() => onToggle()}
        sx={{ cursor: "pointer", "& > *": { borderBottom: expanded ? 0 : undefined } }}
      >
        <TableCell sx={{ width: "18%", maxWidth: 0, overflow: "hidden" }}>
          <Typography variant="caption" noWrap title={row.ts || ""}>
            {formatCallTime(row.ts)}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: "34%", maxWidth: 0, overflow: "hidden" }}>
          <Typography
            variant="caption"
            noWrap
            title={contextPreview}
            sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {truncateText(contextPreview, 42)}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: "28%", maxWidth: 0, overflow: "hidden" }}>
          <Typography variant="caption" noWrap title={formatFeatureLabel(row.feature || "unknown")}>
            {truncateText(formatFeatureLabel(row.feature || "unknown"), 24)}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ width: "12%", whiteSpace: "nowrap" }}>
          <Typography variant="caption">{formatUsd(row.cost_usd)}</Typography>
        </TableCell>
        <TableCell align="center" sx={{ width: "8%", px: 0.5 }}>
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, px: 1, borderBottom: expanded ? undefined : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1 }}>
              <CallDetailPanel
                row={row}
                sessionId={sessionId}
                detail={detail}
                loading={loading}
                error={error}
              />
              {canJump && (
                <Typography
                  variant="caption"
                  component="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onScrollToMessage!(row.source_message_ts!);
                  }}
                  sx={{
                    mt: 0.75,
                    display: "inline-block",
                    border: 0,
                    bgcolor: "transparent",
                    color: "primary.main",
                    cursor: "pointer",
                    p: 0,
                    textDecoration: "underline",
                  }}
                >
                  Jump to message in chat
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function SessionCostPopover({ sessionId, llmUsage, onScrollToMessage }: SessionCostPopoverProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const open = Boolean(anchorEl);

  const totalCost = llmUsage?.total_cost_usd ?? 0;
  const hasUsage = (llmUsage?.total_calls ?? 0) > 0;

  const chartOption = useMemo(() => {
    const timeline = llmUsage?.timeline ?? [];
    return {
      grid: { left: 40, right: 12, top: 16, bottom: 28 },
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: timeline.map((point, index) => `#${index + 1}`),
        axisLabel: { fontSize: 10 },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          fontSize: 10,
          formatter: (value: number) => `$${value.toFixed(3)}`,
        },
      },
      series: [
        {
          type: "line",
          smooth: true,
          showSymbol: false,
          data: timeline.map((point) => point.cumulative_cost_usd ?? 0),
          lineStyle: { color: theme.palette.primary.main, width: 2 },
          areaStyle: { color: alpha(theme.palette.primary.main, 0.12) },
        },
      ],
    };
  }, [llmUsage?.timeline, theme.palette.primary.main]);

  return (
    <>
      <Tooltip
        title={
          hasUsage
            ? `Session LLM cost: ${formatUsd(totalCost)}`
            : "Cost tracking starts on next LLM call"
        }
      >
        <IconButton
          size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            width: 28,
            height: 28,
            color: theme.palette.text.secondary,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.16) },
          }}
        >
          <AttachMoneyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setExpandedIndex(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { width: 520, maxWidth: "95vw", p: 2 },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Session LLM cost
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {formatUsd(totalCost)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          {llmUsage?.total_calls ?? 0} calls · {(llmUsage?.total_tokens ?? 0).toLocaleString()} tokens
        </Typography>

        {(llmUsage?.timeline?.length ?? 0) > 0 && (
          <Box sx={{ height: 160, mb: 2 }}>
            <EChart {...chartOption} style={{ height: "100%", width: "100%" }} />
          </Box>
        )}

        {(llmUsage?.by_feature?.length ?? 0) > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
              By feature
            </Typography>
            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "55%" }}>Feature</TableCell>
                  <TableCell align="right" sx={{ width: "20%" }}>Calls</TableCell>
                  <TableCell align="right" sx={{ width: "25%" }}>Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {llmUsage!.by_feature!.map((row) => (
                  <TableRow key={row.feature}>
                    <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {formatFeatureLabel(row.feature)}
                    </TableCell>
                    <TableCell align="right">{row.calls}</TableCell>
                    <TableCell align="right">{formatUsd(row.cost_usd)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {(llmUsage?.timeline?.length ?? 0) > 0 && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
              Call log (expand for prompt/response)
            </Typography>
            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "18%" }}>Time</TableCell>
                  <TableCell sx={{ width: "34%" }}>Context</TableCell>
                  <TableCell sx={{ width: "28%" }}>Feature</TableCell>
                  <TableCell align="right" sx={{ width: "12%" }}>Cost</TableCell>
                  <TableCell sx={{ width: "8%" }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {llmUsage!.timeline!.map((row, index) => (
                  <CallLogRow
                    key={`${row.call_id || row.ts}-${index}`}
                    row={row}
                    index={index}
                    sessionId={sessionId}
                    expanded={expandedIndex === index}
                    onToggle={() => setExpandedIndex(expandedIndex === index ? null : index)}
                    onScrollToMessage={(ts) => {
                      onScrollToMessage?.(ts);
                      setAnchorEl(null);
                      setExpandedIndex(null);
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Popover>
    </>
  );
}
